import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import htmr from "htmr";
import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { isOpenedSelector, topicSelector } from "stores/tutorialCard/selectors";
import { TUTORIAL_CARD_TOPIC, topic, tutorialCardSlice } from "stores/tutorialCard/slice";

import Portal from "components/sembly-ui/components/Portal/Portal";
import { ChevronFilledUpIcon } from "components/sembly-ui/core/BaseComponent/Icon";
import Image from "components/sembly-ui/core/BaseComponent/Image/Image";

const ANIMATION_DURATION = 0.5; // second
const ANIMATION_DISTANCE = 50; // px
const CHEVRON_WIDTH = 12; // px
const DIALOG_EXTRA_SPACE = 20; // px
const IMAGE_HEIGHT = 362; // px
const IMAGE_WIDTH = 644; // px
const OVERVIEW_CARD_DISTANCE = 65; // px

interface TopicButtonProps {
  text: string;
  name: topic;
  parentId: string;
  mainTopic: string;
  topics: { name: topic; parentId: string }[];
  setTopics: (topics: { name: topic; parentId: string }[]) => void;
  distances: { [topic_parentId: string]: { fromTop: number; fromLeft: number } };
  setDistances: (distances: { [topic_parentId: string]: { fromTop: number; fromLeft: number } }) => void;
  heights: { [topic: string]: number };
  scrollbarRef: React.RefObject<HTMLDivElement>;
  style?: CSSProperties;
}

const TopicButton = ({
  text,
  name,
  parentId,
  mainTopic,
  topics,
  setTopics,
  distances,
  setDistances,
  heights,
  scrollbarRef,
  style,
}: TopicButtonProps) => {
  const buttonRef = useRef<HTMLSpanElement>(null);
  const topicIndex = topics.findIndex(topic => topic.name === name && topic.parentId === parentId);
  const siblingIndex = topics.findIndex(topic => topic.name !== name && topic.parentId === parentId);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();

      const isOpened = topicIndex !== -1;
      const isSibling = siblingIndex !== -1;

      // check if this topic dialog is opened
      if (isOpened) {
        // close the dialog
        setTopics(topics.slice(0, topicIndex).filter(topic => topic.parentId !== parentId));
      } else {
        const previousSameTopicIndex = topics.findIndex(topic => topic.name === name);
        const isPreviousSameTopicExist = (mainTopic === name && topics.length > 0) || previousSameTopicIndex !== -1;

        if (isPreviousSameTopicExist) {
          // just keep until the topic dialog that is same with clicked topic
          setTopics([...topics.slice(0, previousSameTopicIndex + 1)]);
        } else {
          // check if it's sibling (another topic that belong in the same dialog) is opened
          if (isSibling) {
            // open the dialog + close sibling topic and it's descendant(s)
            setTopics([...topics.slice(0, siblingIndex), { name: name, parentId: parentId }]);
          } else {
            // open the dialog
            setTopics([...topics, { name: name, parentId: parentId }]);
          }
        }

        if (buttonRef.current) {
          const { top, left, height, width } = buttonRef.current.getBoundingClientRect();
          const { scrollTop = 0 } = scrollbarRef.current || {};

          const distanceFromTop = top + height + scrollTop;
          const distanceFromLeft = left + width / 2 - CHEVRON_WIDTH / 2;

          // adjust dialog vertical positioning when it's sibling is opened and positioned above
          let margin = 0;
          if (isSibling) {
            margin = topics.slice(siblingIndex).reduce((prev, topic) => {
              if (distances[`${topic.name}-${topic.parentId}`].fromTop < distanceFromTop) {
                return prev + heights[topic.name];
              }
              return prev;
            }, 0);
          }

          // set dialog position
          setDistances({
            ...distances,
            [`${name}-${parentId}`]: { fromTop: distanceFromTop - margin, fromLeft: distanceFromLeft },
          });
        }
      }
    },
    [
      name,
      parentId,
      mainTopic,
      topics,
      setTopics,
      distances,
      setDistances,
      heights,
      siblingIndex,
      topicIndex,
      scrollbarRef,
    ],
  );

  return (
    <>
      {" "}
      <button
        style={style}
        className={clsx([
          "rounded transition-colors duration-500",
          topicIndex === -1 && "hover:bg-light-gray-40",
          topicIndex !== -1 && `bg-medium-purple-20 text-medium-purple-100`,
        ])}
        onClick={handleClick}
      >
        <span
          data-text={text}
          ref={buttonRef}
          className="-mb-1 inline-grid cursor-pointer select-none border-b border-dotted border-b-medium-purple-100 leading-5 transition-all duration-500 after:invisible after:block after:h-0 after:overflow-hidden after:content-[attr(data-text)]"
        >
          {text}
        </span>
      </button>{" "}
    </>
  );
};

type TopicContent = string | { type: "image" | "video"; src: string[] };

interface TopicDialogProps {
  index: number;
  topics: { name: topic; parentId: string }[];
  setTopics: (topics: { name: topic; parentId: string }[]) => void;
  distance: { fromTop: number; fromLeft: number };
  isActive: boolean;
  renderRow: (template: TopicContent, parentId: string, isFirst: boolean) => void;
  setHeight: (topic: string, height: number) => void;
  scrollbarRef: React.RefObject<HTMLDivElement>;
  isOverviewCard: boolean;
}

const TopicDialog = ({
  index,
  topics,
  setTopics,
  distance,
  isActive,
  renderRow,
  setHeight,
  scrollbarRef,
  isOverviewCard,
}: TopicDialogProps) => {
  const [t] = useTranslation("tutorial");
  const ref = useRef<HTMLDivElement>(null);
  const id = useMemo(() => Math.random().toString(), []); // to identify are some topics is sibling
  const topic = topics[index];
  const { scrollTop = 0 } = scrollbarRef.current || {};

  useEffect(() => {
    if (ref.current) setHeight(topic.name, ref.current.clientHeight + DIALOG_EXTRA_SPACE);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actionDialogClicked = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (index < topics.length - 1) {
      // close dialog(s) that in front of this current dialog
      setTopics(topics.slice(0, index + 1));
    }
  };

  const isOnboardingCards =
    isOverviewCard &&
    (topic.name === TUTORIAL_CARD_TOPIC.HIGH_TRUST_SPACES ||
      topic.name === TUTORIAL_CARD_TOPIC.COLLABORATIVE_DISCOURSE_MECHANISM ||
      topic.name === TUTORIAL_CARD_TOPIC.NOISE_FREE_ORGANIZATION);
  const initialExitTop = isOnboardingCards ? OVERVIEW_CARD_DISTANCE * (index + 2) : distance.fromTop - scrollTop;
  const animateTop = isOnboardingCards
    ? OVERVIEW_CARD_DISTANCE * (index + 1)
    : distance.fromTop - scrollTop - ANIMATION_DISTANCE;

  return (
    <motion.div
      ref={ref}
      initial={{ top: initialExitTop, opacity: 0 }}
      animate={{ top: animateTop, opacity: 1 }}
      transition={{ duration: ANIMATION_DURATION }}
      exit={{ top: initialExitTop, opacity: 0 }}
      className={clsx(["absolute left-0 w-full", isActive ? "cursor-auto" : "cursor-pointer"])}
      onClick={actionDialogClicked}
    >
      {!isOnboardingCards && (
        <ChevronFilledUpIcon
          className={clsx([
            `transition-colors duration-500`,
            {
              "text-white": isActive,
              "text-light-gray-40": !isActive,
            },
          ])}
          style={{ marginLeft: distance.fromLeft }}
        />
      )}
      <div
        className={clsx([
          "mx-auto rounded-3xl p-24 text-sembly-gray shadow-light transition-colors duration-500",
          isActive ? "mb-[20vh] bg-white sm:mb-[30vh] md:mb-[40vh]" : "bg-light-gray-40",
        ])}
      >
        <div className="text-base">
          {/* @ts-ignore */}
          {t(`topics.${topic.name}.contents`, { returnObjects: true })?.map((template: TopicContent, index: number) =>
            renderRow(template, id, index === 0),
          )}
        </div>
      </div>
    </motion.div>
  );
};

const TutorialCardDialog = () => {
  const dispatch = useDispatch();
  const isOpened = useSelector(isOpenedSelector);
  const mainTopic = useSelector(topicSelector);
  const isOverviewCard = mainTopic === TUTORIAL_CARD_TOPIC.WELCOME_TO_SEMBLY;
  const [t] = useTranslation("tutorial");
  const [topics, setTopics] = useState<{ name: topic; parentId: string }[]>([]);
  const [distances, setDistances] = useState<{ [topic_parentId: string]: { fromTop: number; fromLeft: number } }>({});
  const [heights, setHeights] = useState<{ [topic: string]: number }>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<HTMLDivElement>(null);

  const { left: containerLeftDistance, top: containerTopDistance } = (containerRef.current?.getBoundingClientRect() ||
    {}) as DOMRect;

  const setHeight = (topic: string, height: number) => {
    setHeights({ ...heights, [topic]: height });
  };

  const renderRow = (template: TopicContent, parentId: string, isFirst: boolean) => {
    if (typeof template === "string") {
      const texts = template.split(/{|}/);

      const parsedTexts = texts.map(text => {
        return (
          <span>
            {htmr(text, {
              transform: {
                span: ({ children, ...rest }) => {
                  // @ts-ignore
                  const topic = rest["data-topic"] as string;
                  return (
                    <TopicButton
                      text={children as string}
                      name={topic as topic}
                      parentId={parentId}
                      mainTopic={mainTopic || ""}
                      topics={topics}
                      setTopics={setTopics}
                      distances={distances}
                      setDistances={setDistances}
                      heights={heights}
                      scrollbarRef={scrollbarRef}
                      style={rest.style}
                    />
                  );
                },
              },
            })}
          </span>
        );
      });

      return <p className={clsx("leading-6", { "mt-24": !isFirst })}>{parsedTexts}</p>;
    } else {
      if (template.type === "video") {
        return template.src.map((source, index) => (
          <video
            autoPlay
            loop
            className={clsx(["aspect-video h-auto w-full max-w-full", { "mt-24": !isFirst }])}
            src={source}
            key={index}
          />
        ));
      } else if (template.type === "image") {
        return (
          <div className={clsx({ "mt-24": !isFirst })}>
            {template.src.map((source, index) => (
              <Image height={IMAGE_HEIGHT} width={IMAGE_WIDTH} src={source} alt="" key={index} />
            ))}
          </div>
        );
      }
    }
  };

  const actionFirstCardClicked = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();

    setTopics([]);
    setDistances({});
  };

  const actionClose = () => {
    setTopics([]);
    setDistances({});
    dispatch(tutorialCardSlice.actions.close());
  };

  return (
    <>
      {isOpened && (
        <Portal>
          <div className="fixed left-0 top-0 z-tooltip h-screen w-screen bg-gray-50 opacity-50 transition-opacity" />
          <div
            className="fixed left-0 top-0 z-tooltip flex min-h-screen w-screen items-center justify-center overflow-y-scroll"
            ref={scrollbarRef}
            onClick={actionClose}
          >
            <div ref={containerRef} className="relative max-h-[calc(100vh-48px)] w-11/12 max-w-[692px] md:w-full">
              <AnimatePresence>
                <motion.div
                  key={mainTopic}
                  initial={{ top: 500, opacity: 0 }}
                  animate={{ top: 0, opacity: 1 }}
                  transition={{ duration: ANIMATION_DURATION }}
                  exit={{ top: 500, opacity: 0 }}
                  className={clsx([
                    "w-full rounded-3xl p-24 text-sembly-gray shadow-light transition-colors duration-500",
                    topics.length === 0 ? "cursor-auto bg-white" : "cursor-pointer bg-light-gray-40",
                  ])}
                  onClick={actionFirstCardClicked}
                >
                  <div className="text-base">
                    {/* @ts-ignore */}
                    {t(`topics.${mainTopic}.contents`, { returnObjects: true }).map(
                      (template: TopicContent, index: number) => renderRow(template, `${0}`, index === 0),
                    )}
                  </div>
                  <AnimatePresence>
                    {topics.map(({ name, parentId }, index) => {
                      const isActive =
                        topics[topics.length - 1].name === name && topics[topics.length - 1].parentId === parentId;

                      const distance = {
                        fromTop: distances[`${name}-${parentId}`].fromTop - containerTopDistance + 60,
                        fromLeft: distances[`${name}-${parentId}`].fromLeft - containerLeftDistance,
                      };

                      return (
                        <TopicDialog
                          key={`${name}-${parentId}`}
                          index={index}
                          topics={topics}
                          setTopics={setTopics}
                          distance={distance}
                          isActive={isActive}
                          renderRow={renderRow}
                          setHeight={setHeight}
                          scrollbarRef={scrollbarRef}
                          isOverviewCard={isOverviewCard}
                        />
                      );
                    })}
                  </AnimatePresence>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </Portal>
      )}
    </>
  );
};

export default TutorialCardDialog;
